/**
 * One invitations chrome for every People leaf (TMMPDM-0085).
 * Pending rows: heading, Show all switch, table.
 * None pending with Show all off: one line and a text button.
 * Show all with none at all: the table's own empty state (passed as children).
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SectionCard } from '@/components/page';
import { AppSwitch } from '@/components/ui';

export function PeopleInvitationsBlock({
  ready,
  count,
  showAll,
  onShowAll,
  headingId,
  children,
}: {
  ready: boolean;
  count: number;
  showAll: boolean;
  onShowAll: (value: boolean) => void;
  headingId?: string;
  children: ReactNode;
}) {
  const { t } = useCopy();
  const inputRef = useRef<HTMLInputElement>(null);
  const armFocus = useRef(false);
  // Show all mounts the switch in the same commit as the click, even while the list reloads.
  const showSwitch = showAll || (ready && count > 0);

  useLayoutEffect(() => {
    if (!armFocus.current || !showSwitch) return;
    inputRef.current?.focus();
    armFocus.current = false;
  }, [showSwitch]);

  if (!showSwitch) {
    if (!ready) {
      return (
        <Box id={headingId}>
          <LoadingSpinner />
        </Box>
      );
    }
    return (
      <SectionCard>
        <Stack id={headingId} spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Typography component="div">
            {t('tenantDetail.invitations.empty', 'No invitations pending.')}
          </Typography>
          <Button
            variant="text"
            onClick={() => {
              armFocus.current = true;
              onShowAll(true);
            }}
          >
            {t('tenantDetail.invitations.statusAll', 'Show all')}
          </Button>
        </Stack>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={t('tenantDetail.invitations.title', 'Invitations')}
      {...(headingId !== undefined ? { id: headingId } : {})}
      actions={
        <AppSwitch
          inputRef={inputRef}
          label={t('tenantDetail.invitations.statusAll', 'Show all')}
          checked={showAll}
          onChange={onShowAll}
        />
      }
    >
      {ready ? children : <LoadingSpinner />}
    </SectionCard>
  );
}
